import { emailSchema, TTL_RESET_DE_SENHA_MINUTOS } from '@locaveri/shared'
import { useMutation } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { LoaderCircle, MailCheck } from 'lucide-react'
import { type ReactElement, useState } from 'react'
import { useForm } from 'react-hook-form'

import { AuthLayout } from '@/components/auth/auth-layout'
import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { solicitarResetDeSenha } from '@/features/auth/api'
import { errorMessage } from '@/lib/api'
import { validarCom } from '@/lib/formulario'

/**
 * Pedido de redefinição de senha.
 *
 * ---------------------------------------------------------------------------
 * A MESMA RESPOSTA PARA E-MAIL QUE EXISTE E PARA E-MAIL QUE NÃO EXISTE
 * ---------------------------------------------------------------------------
 * Dizer "não encontramos esse e-mail" transforma esta tela em um oráculo de
 * quem é cliente da locadora — basta testar endereços. Como o negócio é locação
 * blindada, essa lista tem valor para quem não deveria tê-la.
 *
 * Por isso a confirmação é sempre idêntica, e é escrita no condicional ("se
 * existir uma conta") para não mentir para quem digitou o endereço errado e
 * fica esperando um e-mail que nunca vai chegar.
 */

interface FormularioDeEsqueciSenha {
  email: string
}

export function EsqueciSenhaPage(): ReactElement {
  const [enviado, setEnviado] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormularioDeEsqueciSenha>({ defaultValues: { email: '' } })

  const solicitar = useMutation({
    mutationFn: solicitarResetDeSenha,
    onSuccess: () => {
      setEnviado(true)
    },
    onError: (causa: unknown) => {
      setErro(errorMessage(causa))
    },
  })

  const aoEnviar = handleSubmit((valores) => {
    setErro(null)
    const validado = emailSchema.safeParse(valores.email)

    if (!validado.success) {
      setErro('Informe um e-mail válido.')
      return
    }

    solicitar.mutate(validado.data)
  })

  const rodape = (
    <Link to="/login" className="text-brand text-sm underline-offset-4 hover:underline">
      Voltar para o login
    </Link>
  )

  if (enviado) {
    return (
      <AuthLayout titulo="Verifique seu e-mail" rodape={rodape}>
        <Alert variant="success">
          <AlertTitle>Pedido registrado</AlertTitle>
          <AlertDescription>
            Se existir uma conta com esse e-mail, enviamos um link de redefinição. Ele vale por{' '}
            {TTL_RESET_DE_SENHA_MINUTOS} minutos e pode ser usado uma única vez.
          </AlertDescription>
        </Alert>

        <p className="flex items-start gap-2 text-xs text-fg-muted">
          <MailCheck className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span>
            Não recebeu? Confira a caixa de spam e o endereço digitado — e então peça de novo.
          </span>
        </p>

        <Button
          variant="secondary"
          className="w-full"
          onClick={() => {
            setEnviado(false)
          }}
        >
          Usar outro e-mail
        </Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      titulo="Redefinir senha"
      descricao="Informe o e-mail da sua conta e enviaremos um link de redefinição."
      rodape={rodape}
    >
      <form
        noValidate
        onSubmit={(evento) => {
          void aoEnviar(evento)
        }}
        className="space-y-4"
      >
        {erro === null ? null : (
          <Alert variant="danger">
            <AlertDescription>{erro}</AlertDescription>
          </Alert>
        )}

        <Campo rotulo="E-mail" obrigatorio erro={errors.email?.message}>
          {(campo) => (
            <Input
              {...campo}
              {...register('email', {
                required: 'informe o e-mail',
                validate: validarCom(emailSchema),
              })}
              type="email"
              autoComplete="username"
              placeholder="voce@locaveri.com.br"
              disabled={solicitar.isPending}
            />
          )}
        </Campo>

        <Button type="submit" className="w-full" disabled={solicitar.isPending}>
          {solicitar.isPending ? (
            <LoaderCircle className="animate-spin" aria-hidden="true" />
          ) : null}
          {solicitar.isPending ? 'Enviando…' : 'Enviar link de redefinição'}
        </Button>
      </form>
    </AuthLayout>
  )
}
